import {
  AddAppAccountRequestPayload,
  ConsentTermsId,
  TermsConsent,
} from "@/data/payload/app-account-setting/add-app-account-request-payload";
import { CheckUserIdAvailableRequestPayload } from "@/data/payload/app-account-setting/check-user-id-available-reqeuest-payload";
import { AccountSetupIdentityResponseDto } from "@/data/dto/account-setup-identity-response-dto";
import { AccountSetupIdentityEntity } from "@/domain/entity/app-account-setting/account-setup-identity-entity";
import { POLICY_CONSENT_URL, URLList } from "@/data/url_list";
import { FetchApiClientMethod } from "@sadtx/fetch-api-client";
import { errorConverter, SATError, SATErrorModel, SATErrorType, SATResult } from "@sadtx/sat-error";
import { BrowserStorageKeyProvider } from "@/utils/crypto/browser-storage-key-provider";
import SecureStorageService from "@sadtx/secure-storage";

export class AppAccountSettingSource {
  private readonly AUTH_TOKEN_STORAGE_KEY = "app-account-setting-token";
  private readonly TERMS_CONSENT_STORAGE_KEY = "app-account-setting-terms-consent";
  private readonly IDENTITY_STORAGE_KEY = "app-account-setting-identity";
  protected readonly EXPIRED_AUTH_CODE_TOKEN_ERROR = new SATError(
    SATErrorType.ExpiredAuthToken,
    "Expired auth code token"
  );

  //MARK:- Constructor
  constructor(
    protected readonly client: FetchApiClientMethod,
    protected readonly secureStorage: SecureStorageService,
    private readonly storageKeyProvider: BrowserStorageKeyProvider
  ) {}

  async setAuthorizedToken(token: string): Promise<SATResult<void, SATError>> {
    try {
      // 계정 설정 흐름을 새로 시작하므로 이전 흐름의 브라우저 임시 키를 교체
      await this.storageKeyProvider.rotateKey();
      await this.secureStorage.setItem(sessionStorage, this.AUTH_TOKEN_STORAGE_KEY, token);
      return SATResult.success(void 0);
    } catch (error: unknown) {
      console.error("[AppAccountSettingSource] :: setAuthorizedToken :: 토큰 저장 실패", error);
      const satError = errorConverter.convertError(error);
      return SATResult.failure(satError);
    }
  }

  async saveTermsConsent(termsConsent: TermsConsent[]): Promise<SATResult<void, SATError>> {
    try {
      await this.secureStorage.setItem(
        sessionStorage,
        this.TERMS_CONSENT_STORAGE_KEY,
        JSON.stringify(termsConsent)
      );
      return SATResult.success(void 0);
    } catch (error: unknown) {
      console.error(
        "[AppAccountSettingSource] :: saveTermsConsent :: 약관 동의 정보 저장 실패",
        error
      );
      const satError = errorConverter.convertError(error);
      return SATResult.failure(satError);
    }
  }

  async isExistAuthorizedToken(): Promise<boolean> {
    try {
      await this.getAuthorizedToken();
      return true;
    } catch (error: unknown) {
      return false;
    }
  }

  async removeAuthorizedToken(): Promise<void> {
    await this.secureStorage.removeItem(sessionStorage, this.AUTH_TOKEN_STORAGE_KEY);
    await this.storageKeyProvider.clear();
  }

  /**
   * 사용자 계정 설정을 위한 SMS를 통해 접속하는 경우 사용자 이름 검증 요청
   * @param payload - 사용자 이름 검증 요청 데이터, 토큰, 이름
   * @returns {Promise<SATResult<AccountSetupIdentityResponseDto, SATError>>} 본인확인 결과 (병원·보호자·환자 정보)
   */
  async checkUserNameBySmsTokenForLoginAccount(
    name: string
  ): Promise<SATResult<AccountSetupIdentityResponseDto, SATError>> {
    try {
      const token = await this.getAuthorizedToken();
      const url = `${URLList.AUTH.CHECK_USER_NAME_BY_SMS_TOKEN}/${token}/${name}`;
      const response = await this.client.get(url);
      console.log(
        "[AppAccountSettingSource] :: checkUserNameBySmsToken :: 사용자 이름 검증 요청 성공"
      );
      return SATResult.success(response.data as AccountSetupIdentityResponseDto);
    } catch (error: unknown) {
      console.error(
        "[AppAccountSettingSource] :: checkUserNameBySmsToken :: 사용자 이름 검증 요청 실패",
        error
      );
      const satError = errorConverter.convertError(error);
      console.log("[AppAccountSettingSource] :: checkUserNameBySmsToken | ", satError);
      return SATResult.failure(satError);
    }
  }

  /**
   * 본인확인 결과를 정보확인 화면에서 사용할 수 있도록 sessionStorage에 저장
   */
  async saveAccountSetupIdentity(
    identity: AccountSetupIdentityEntity
  ): Promise<SATResult<void, SATError>> {
    try {
      await this.secureStorage.setItem(
        sessionStorage,
        this.IDENTITY_STORAGE_KEY,
        JSON.stringify(identity)
      );
      return SATResult.success(void 0);
    } catch (error: unknown) {
      console.error("[AppAccountSettingSource] :: saveAccountSetupIdentity :: 저장 실패", error);
      return SATResult.failure(errorConverter.convertError(error));
    }
  }

  /**
   * 저장된 본인확인 결과 조회. 없으면 만료 에러
   */
  async getAccountSetupIdentity(): Promise<SATResult<AccountSetupIdentityEntity, SATError>> {
    try {
      const raw = await this.secureStorage.getItem(sessionStorage, this.IDENTITY_STORAGE_KEY);
      if (!raw) {
        return SATResult.failure(this.EXPIRED_AUTH_CODE_TOKEN_ERROR);
      }
      // SecureStorageService.getItem 은 "{...}" 형태의 값을 JSON 객체로 자동 파싱해 돌려주므로
      // 문자열·객체 두 경우를 모두 받는다 (문자열만 허용하면 저장 직후 조회도 항상 실패한다)
      const identity =
        typeof raw === "string"
          ? (JSON.parse(raw) as AccountSetupIdentityEntity)
          : (raw as AccountSetupIdentityEntity);
      if (typeof identity !== "object" || identity === null) {
        return SATResult.failure(this.EXPIRED_AUTH_CODE_TOKEN_ERROR);
      }
      return SATResult.success(identity);
    } catch {
      return SATResult.failure(this.EXPIRED_AUTH_CODE_TOKEN_ERROR);
    }
  }

  /**
   * SMS 인증을 통해 앱 계정(ID, 비밀번호 및 이용약관 동의 정보) 등록 요청
   * 쿠키 기반 사용자 인증
   * @param payload - 앱 계정 등록 및 약관 동의 요청 데이터
   * @returns {Promise<SATResult<void, SATError>>} 설정 결과
   */
  async addAppAccount(payload: AddAppAccountRequestPayload): Promise<SATResult<void, SATError>> {
    try {
      const termsConsent = await this.getTermsConsent();
      const newPayload = {
        ...payload,
        termsConsent: termsConsent,
      };

      const url = `${URLList.AUTH.SET_ID_AND_PASSWORD_BY_SMS}`;
      console.log("[AppAccountSettingSource] :: addAppAccount :: 요청 URL", url);
      console.log(
        "[AppAccountSettingSource] :: addAppAccount :: 요청 Payload",
        JSON.stringify(newPayload, null, 2)
      );
      console.log("[AppAccountSettingSource] :: addAppAccount :: termsConsent 검증", {
        length: termsConsent.length,
        items: termsConsent.map((item) => ({
          termsId: item.termsId,
          version: item.version,
          isConsented: item.isConsented,
          consentedAt: item.consentedAt,
        })),
      });

      const response = await this.client.put(url, newPayload);
      console.log(
        "[AppAccountSettingSource] :: addAppAccount :: ID 및 비밀번호 설정 요청 성공",
        response.data
      );
      return SATResult.success(void 0);
    } catch (error: unknown) {
      // Axios 에러인 경우 상세 정보 로깅
      if (error && typeof error === "object" && "response" in error) {
        const axiosError = error as {
          response?: { data?: unknown; status?: number; statusText?: string };
        };
        console.error(
          "[AppAccountSettingSource] :: addAppAccount :: ID 및 비밀번호 설정 요청 실패",
          {
            status: axiosError.response?.status,
            statusText: axiosError.response?.statusText,
            responseData: axiosError.response?.data,
            error: error,
          }
        );
      } else {
        console.error(
          "[AppAccountSettingSource] :: addAppAccount :: ID 및 비밀번호 설정 요청 실패",
          error
        );
      }

      // getTermsConsent에서 throw된 에러인 경우 그대로 반환
      if (error instanceof SATError) {
        return SATResult.failure(error);
      }
      const satError = errorConverter.convertError(error);
      console.log("[AppAccountSettingSource] :: addAppAccount :: 변환된 에러", satError);
      return SATResult.failure(satError);
    }
  }

  /**
   * 사용자 아이디 사용 가능 여부 검증 요청
   * @param payload - 사용자 아이디 사용 가능 여부 검증 요청 데이터
   * @returns {Promise<SATResult<void, SATError>>} 사용자 아이디 사용 가능 여부 검증 결과
   */
  async checkUserIdAvailable(
    payload: CheckUserIdAvailableRequestPayload
  ): Promise<SATResult<void, SATError>> {
    try {
      const url = `${URLList.AUTH.CHECK_USER_ID_AVAILABLE}/${payload.loginId}`;
      const response = await this.client.get(url);
      console.log(
        "[AppAccountSettingSource] :: checkUserIdAvailable :: 사용자 아이디 가용성 검증 요청 성공",
        response.data
      );
      return SATResult.success(void 0);
    } catch (error: unknown) {
      console.error(
        "[AppAccountSettingSource] :: checkUserIdAvailable :: 사용자 아이디 가용성 검증 요청 실패",
        error
      );
      const satError = errorConverter.convertError(error);
      console.log("[AppAccountSettingSource] :: checkUserIdAvailable | ", satError);
      return SATResult.failure(satError);
    }
  }

  /**
   * 보호자 동의 약관 마크다운 파일 조회 (별도 토큰·인증 불필요)
   * @param termsId - 약관 식별자 (Blob 파일명)
   * @param locale - 언어 코드
   * @returns 약관 본문과 버전(응답 헤더 `x-ms-meta-version`)
   */
  async fetchConsentTermsFile(
    termsId: ConsentTermsId,
    locale: string
  ): Promise<SATResult<ParentalConsentDto, SATError>> {
    try {
      // 브라우저 캐시를 무시하기 위해 타임스탬프 쿼리 파라미터 추가
      const url = `${POLICY_CONSENT_URL[termsId](locale)}?_t=${Date.now()}`;
      const result = await this.client.get(url);

      const version = result.headers["x-ms-meta-version"];
      console.log("[AppAccountSettingSource] :: fetchConsentTermsFile :: 약관 파일 조회 성공", {
        termsId,
        version,
        contentLength: result.data?.length,
      });
      return SATResult.success({ version, content: result.data });
    } catch (error: unknown) {
      console.error("[AppAccountSettingSource] :: fetchConsentTermsFile :: 약관 파일 조회 실패", {
        termsId,
        error,
      });
      const satError = errorConverter.convertError(error);
      console.log("[AppAccountSettingSource] :: fetchConsentTermsFile | ", satError);
      return SATResult.failure(satError);
    }
  }

  //MARK: - Protected (Mock Source 에서 재사용)
  protected async getAuthorizedToken(): Promise<string> {
    try {
      const token = await this.secureStorage.getItem(sessionStorage, this.AUTH_TOKEN_STORAGE_KEY);
      if (!token) {
        throw this.EXPIRED_AUTH_CODE_TOKEN_ERROR;
      }
      return token;
    } catch (error: unknown) {
      throw this.EXPIRED_AUTH_CODE_TOKEN_ERROR;
    }
  }

  protected async getTermsConsent(): Promise<TermsConsent[]> {
    try {
      const termsConsent = await this.secureStorage.getItem(
        sessionStorage,
        this.TERMS_CONSENT_STORAGE_KEY
      );

      // 타입 가드: null, undefined, 빈 문자열 체크
      if (!termsConsent || typeof termsConsent !== "string") {
        throw new Error("약관 동의 정보가 없거나, 타입이 올바르지 않습니다.");
      }

      // 문자열 "undefined" 체크 (혹시 모를 경우 대비)
      if (termsConsent === "undefined" || termsConsent.trim() === "") {
        throw new Error("약관 동의 정보가 없거나, 타입이 올바르지 않습니다.");
      }

      // JSON 파싱 시도
      const parsed = JSON.parse(termsConsent) as TermsConsent[];
      return parsed;
    } catch (error: unknown) {
      // 이미 SATError인 경우 그대로 throw
      if (error instanceof SATError) {
        throw error;
      }
      console.error(
        "[AppAccountSettingSource] :: getTermsConsent :: 약관 동의 정보 조회 실패",
        error
      );
      throw new Error("약관 동의 정보 조회 실패");
    }
  }
}
